import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/common/Popover';
import { cn } from '@/lib/cn';
import * as Tooltip from '@radix-ui/react-tooltip';
import { EyeIcon, EyeOffIcon, InfoIcon } from 'lucide-react';
import * as React from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  containerClassName?: string;
  label?: string;
  allowToggle?: boolean;
  error?: string;
  touched?: boolean;
  info?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      containerClassName,
      type,
      label,
      allowToggle,
      error,
      touched,
      id,
      info,
      ...props
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const inputType = allowToggle && showPassword ? 'text' : type;

    return (
      <div className={cn('group relative w-full', containerClassName)}>
        <div className="relative">
          <input
            type={inputType}
            className={cn(
              'border-input ring-offset-background focus:border-primary peer flex w-full rounded-[0.625rem] border bg-white px-4 py-4 text-lg file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-transparent autofill:shadow-[0_0_0_62.5rem_var(--background)_inset] focus:border-[0.09375rem] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&-webkit-autofill]:transition-colors [&-webkit-autofill]:duration-[5000s] [&-webkit-autofill]:[-webkit-text-fill-color:var(--foreground)]',
              allowToggle ? 'pr-10' : '',
              touched && error ? 'border-destructive' : '',
              className,
            )}
            ref={ref}
            placeholder=" "
            id={id}
            {...props}
          />
          {label && (
            <div
              className={cn(
                'pointer-events-none absolute left-4 flex h-full items-center transition-all duration-200 ease-out',
                'peer-focus:bg-background peer-[:not(:placeholder-shown)]:bg-background peer-focus:-top-3 peer-focus:h-auto peer-focus:px-1 peer-focus:text-xs peer-[:not(:placeholder-shown)]:-top-3 peer-[:not(:placeholder-shown)]:h-auto peer-[:not(:placeholder-shown)]:px-1 peer-[:not(:placeholder-shown)]:text-xs',
                (touched && error) || props.value || props.defaultValue
                  ? 'bg-background -top-3 h-auto px-1 text-xs'
                  : 'top-0',
              )}
            >
              <label
                htmlFor={id}
                className={cn(
                  'text-muted-foreground shrink-0 font-normal whitespace-nowrap transition-colors duration-200',
                  touched && error ? 'text-destructive' : '',
                )}
              >
                {label}
              </label>
              {touched && error && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-destructive pointer-events-auto ml-1.5 max-w-30 shrink cursor-help overflow-hidden text-left text-xs font-light text-ellipsis whitespace-nowrap focus:outline-none md:max-w-40"
                    >
                      - {error}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="bg-primary text-primary-foreground max-w-xs border-0 px-3 py-1.5 text-sm"
                    sideOffset={5}
                    align="start"
                  >
                    {error}
                  </PopoverContent>
                </Popover>
              )}
              {info && (
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-primary pointer-events-auto ml-1.5 focus:outline-none"
                      >
                        <InfoIcon className="size-3.5" />
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content
                        className="bg-primary text-primary-foreground animate-in fade-in zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-w-xs rounded-lg px-3 py-1.5 text-sm shadow-md"
                        sideOffset={5}
                      >
                        {info}
                        <Tooltip.Arrow className="fill-primary" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
              )}
            </div>
          )}

          {allowToggle && (
            <div
              className="text-muted-foreground hover:text-foreground absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeIcon className="size-5" />
              ) : (
                <EyeOffIcon className="size-5" />
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
